import { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { listenToAdvisoryReviewFact } from '../../services/advisoryService';
import {
  newerReview,
  parseReviewSnapshot,
  type ReviewChanges,
  type ReviewField,
  type ReviewRequest,
  type ReviewResolution,
  type ReviewSnapshot,
  type ReviewValues,
} from './advisoryReview';
import type { SupervisorTurnOutcome, SupervisorTurnRequest } from './useSupervisorTurn';
import { acknowledgePendingIntake } from './advisoryIntake';

type DirtyField = keyof ReviewChanges;
type ReadStatus = 'loading' | 'absent' | 'ready' | 'denied' | 'failed' | 'invalid';
interface EditorState {
  scope: string;
  readStatus: ReadStatus;
  snapshot: ReviewSnapshot | null;
  values: ReviewValues | null;
  feedback: string;
  dirty: DirtyField[];
  busy: ReviewRequest['operation'] | null;
  error: string | null;
  stale: boolean;
}
const initial = (scope: string): EditorState => ({
  scope,
  readStatus: 'loading',
  snapshot: null,
  values: null,
  feedback: '',
  dirty: [],
  busy: null,
  error: null,
  stale: false,
});
const equal = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
const ERROR = 'advisory:review.saveFailed';
const CONFIRM_UNCERTAIN = 'advisory:review.confirmationUncertain';
const REJECTED = 'advisory:review.saveRejected';
const CHANGED = 'advisory:review.changedElsewhere';
const draftSignature = (draft: Pick<EditorState, 'values' | 'feedback'>) =>
  JSON.stringify({ values: draft.values, feedback: draft.feedback });

export interface ReviewEditBase {
  scope: string;
  values: ReviewValues;
  counterpartyKind: ReviewSnapshot['counterpartyKind'];
}

function acknowledge(current: EditorState, request: ReviewRequest): DirtyField[] {
  // Refinement uses the saved draft, so every local dirty field is a newer edit.
  if (request.operation !== 'update') return current.dirty;
  return current.dirty.filter(field => {
    if (!(field in request.changes)) return true;
    const value = field === 'feedbackDraft' ? current.feedback : current.values?.[field];
    return !equal(value, request.changes[field]);
  });
}

function mergeDraft(current: EditorState, snapshot: ReviewSnapshot, dirty: DirtyField[]) {
  const values = { ...snapshot.values };
  if (current.values)
    for (const field of dirty) {
      if (field === 'subjectCompany') values.subjectCompany = current.values.subjectCompany;
      if (field === 'counterparties') values.counterparties = current.values.counterparties;
      if (field === 'period') values.period = current.values.period;
    }
  return {
    values,
    feedback: dirty.includes('feedbackDraft') ? current.feedback : snapshot.feedbackDraft,
  };
}

export interface UnifiedReviewOptions {
  userId?: string;
  projectId?: string;
  send: (request: SupervisorTurnRequest) => Promise<SupervisorTurnOutcome>;
  liveReview?: ReviewSnapshot | null;
}

/** Owns one local draft and serializes revisioned writes through the existing supervisor connection. */
export function useUnifiedEngagementReview({
  userId,
  projectId,
  send,
  liveReview,
}: UnifiedReviewOptions) {
  const scope = `${userId ?? ''}/${projectId ?? ''}`;
  const [state, setState] = useState<EditorState>(() => initial(scope));
  const stateRef = useRef(state);
  const mounted = useRef(true);
  const inFlight = useRef<object | null>(null);
  const serverSnapshot = useRef<ReviewSnapshot | null>(null);
  const preparationRecovery = useRef({ revision: 0, sequence: 0 });
  const streamPreparation = useRef<{
    snapshotAtStart: ReviewSnapshot | null;
    recoverySequence: number;
    awaitingRecoveryStart: boolean;
  } | null>(null);
  const failedRequest = useRef<{
    request: ReviewRequest;
    draft: string;
    editVersion: number;
  } | null>(null);
  const rejectedDraft = useRef<string | null>(null);
  const editVersion = useRef(0);
  const pendingPreparation = useRef<{ request: ReviewRequest; revision: number } | null>(null);
  const [readAttempt, setReadAttempt] = useState(0);
  const update = useCallback((fn: (value: EditorState) => EditorState) => {
    if (!mounted.current) return;
    stateRef.current = fn(stateRef.current);
    setState(stateRef.current);
  }, []);

  const receive = useCallback(
    (snapshot: ReviewSnapshot, fromRead = false, fromCache = false) => {
      if (!fromCache && stateRef.current.scope === scope) acknowledgePendingIntake(scope, snapshot);
      update(current => {
        if (fromRead && !fromCache) {
          const previousRead = serverSnapshot.current;
          if (!previousRead || snapshot.revision >= previousRead.revision) {
            const resumedFailure =
              previousRead?.revision === snapshot.revision &&
              previousRead.status === 'needsAttention' &&
              (snapshot.status === 'preparing' || snapshot.status === 'ready');
            const settledEarlierPreparation =
              (snapshot.status === 'ready' &&
                (streamPreparation.current?.snapshotAtStart?.revision !== snapshot.revision ||
                  streamPreparation.current?.snapshotAtStart?.status !== 'ready')) ||
              (snapshot.status === 'needsAttention' && previousRead?.status === 'preparing');
            if (resumedFailure || settledEarlierPreparation) {
              // Server reads settle preparation even if it still needs attention.
              // A new call that starts ready can authoritatively revalidate it.
              preparationRecovery.current = {
                revision: snapshot.revision,
                sequence: preparationRecovery.current.sequence + 1,
              };
            }
            const stream = streamPreparation.current;
            if (
              snapshot.status === 'preparing' &&
              stream?.awaitingRecoveryStart &&
              inFlight.current === stream &&
              stream.snapshotAtStart?.revision === snapshot.revision
            ) {
              // The first restart belongs to our retry, even if its initial
              // failure read was skipped. A later restart supersedes it.
              stream.recoverySequence = preparationRecovery.current.sequence;
              stream.awaitingRecoveryStart = false;
            }
            serverSnapshot.current = snapshot;
          }
        }
        const recovery = preparationRecovery.current;
        const latest = newerReview(current.snapshot, snapshot, {
          allowPreparationRecovery: fromRead && !fromCache,
          ignoreStalePreparationFailure:
            recovery.revision === snapshot.revision &&
            (fromCache ||
              (!fromRead &&
                (streamPreparation.current?.recoverySequence ?? 0) < recovery.sequence)),
        });
        if (latest === current.snapshot)
          return fromRead ? { ...current, readStatus: 'ready' } : current;
        const externallyChanged = Boolean(
          current.snapshot &&
            latest.revision > current.snapshot.revision &&
            current.dirty.length &&
            !inFlight.current
        );
        const pending = pendingPreparation.current;
        const settledPending = Boolean(
          !inFlight.current &&
            pending &&
            latest.revision === pending.revision &&
            latest.status !== 'preparing'
        );
        const changedElsewhere = current.stale || externallyChanged;
        const dirty =
          settledPending && pending && !changedElsewhere
            ? acknowledge(current, pending.request)
            : current.dirty;
        // Approval keeps the revision. A pending write must acknowledge its
        // saved fields before any remaining edits require reconciliation.
        const stale =
          changedElsewhere ||
          Boolean(latest.status === 'approved' && dirty.length && !inFlight.current);
        if (
          !inFlight.current &&
          pending &&
          (settledPending || latest.revision > pending.revision)
        ) {
          pendingPreparation.current = null;
          failedRequest.current = null;
        }
        return {
          ...current,
          readStatus: fromRead ? 'ready' : current.readStatus,
          snapshot: latest,
          ...(inFlight.current
            ? { values: current.values ?? latest.values, feedback: current.feedback }
            : mergeDraft(current, latest, dirty)),
          dirty,
          error: stale ? CHANGED : settledPending ? null : current.error,
          stale,
        };
      });
    },
    [update, scope]
  );

  useEffect(() => {
    mounted.current = true;
    if (stateRef.current.scope !== scope) {
      failedRequest.current = null;
      rejectedDraft.current = null;
      editVersion.current = 0;
      pendingPreparation.current = null;
      inFlight.current = null;
      serverSnapshot.current = null;
      preparationRecovery.current = { revision: 0, sequence: 0 };
      streamPreparation.current = null;
      update(() => initial(scope));
    } else {
      update(current => ({ ...current, readStatus: 'loading' }));
    }
    if (!userId || !projectId) return undefined;
    let live = true;
    const unsubscribe = listenToAdvisoryReviewFact(
      userId,
      projectId,
      (document, fromCache = false) => {
        if (!live) return;
        if (!document) {
          update(current => (current.snapshot ? current : { ...current, readStatus: 'absent' }));
          return;
        }
        const snapshot = parseReviewSnapshot(document.data.snapshot, projectId);
        if (snapshot) receive(snapshot, true, fromCache);
        else update(current => ({ ...current, readStatus: 'invalid' }));
      },
      code => {
        if (!live) return;
        update(current => ({
          ...current,
          readStatus: code.includes('permission-denied') ? 'denied' : 'failed',
        }));
      }
    );
    return () => {
      live = false;
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [scope, userId, projectId, readAttempt, receive, update]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      inFlight.current = null;
      pendingPreparation.current = null;
    };
  }, []);
  useEffect(() => {
    if (liveReview && liveReview.projectId === projectId && stateRef.current.scope === scope)
      receive(liveReview);
  }, [liveReview, projectId, scope, receive]);

  const execute = useCallback(
    async (request: ReviewRequest): Promise<void> => {
      if (
        !projectId ||
        !userId ||
        inFlight.current ||
        stateRef.current.stale ||
        (request.operation === 'confirm' && stateRef.current.dirty.length > 0) ||
        stateRef.current.scope !== scope
      )
        return;
      const snapshotAtStart = stateRef.current.snapshot;
      const replaying = failedRequest.current?.request.requestId === request.requestId;
      const operation = {
        snapshotAtStart,
        recoverySequence: preparationRecovery.current.sequence,
        // A receipt replay need not start work, so it cannot claim a later restart.
        awaitingRecoveryStart:
          request.operation === 'prepare' &&
          snapshotAtStart?.status === 'needsAttention' &&
          !replaying,
      };
      streamPreparation.current = operation;
      // An uncertain retry resends the original body, so its comparison baseline
      // must also remain the draft from the first attempt.
      const submitted =
        replaying && failedRequest.current
          ? failedRequest.current
          : { request, draft: draftSignature(stateRef.current), editVersion: editVersion.current };
      const submittedEditVersion = submitted.editVersion;
      const submittedDraft = submitted.draft;
      const expected =
        'expectedRevision' in request
          ? request.expectedRevision
          : stateRef.current.snapshot?.revision ?? 0;
      inFlight.current = operation;
      rejectedDraft.current = null;
      failedRequest.current = submitted;
      update(current => ({ ...current, busy: request.operation, error: null }));
      let outcome: SupervisorTurnOutcome;
      try {
        outcome = await send({ projectId, review: request });
      } catch {
        outcome = { status: 'error', errorI18nKey: ERROR };
      }
      if (!mounted.current || stateRef.current.scope !== scope || inFlight.current !== operation)
        return;
      inFlight.current = null;
      const incoming = outcome.review;
      if (incoming) acknowledgePendingIntake(scope, incoming);
      const accepted =
        outcome.status === 'done' &&
        Boolean(incoming) &&
        (request.operation !== 'confirm' ||
          Boolean(outcome.reviewConfirmed && incoming?.status === 'approved'));
      update(current => {
        const snapshot = incoming
          ? newerReview(current.snapshot, incoming, {
              ignoreStalePreparationFailure:
                incoming.revision === preparationRecovery.current.revision &&
                operation.recoverySequence < preparationRecovery.current.sequence,
              // A current response can reveal recovery without a working
              // listener, but must not undo a settlement received meanwhile.
              allowPreparationRecovery:
                current.snapshot === snapshotAtStart &&
                (request.operation === 'prepare' ||
                  (outcome.status === 'error' &&
                    (request.operation === 'update' || request.operation === 'refine') &&
                    incoming.revision === expected)),
            })
          : current.snapshot;
        const knownPending = Boolean(
          pendingPreparation.current?.request.requestId === request.requestId &&
            snapshot?.revision === pendingPreparation.current?.revision
        );
        const settled = Boolean(
          snapshot && snapshot.status !== 'preparing' && (accepted || knownPending)
        );
        // A replay returns the current draft, which may include another tab's
        // changes. Detect that before acknowledging any of our dirty fields.
        const incrementsRevision = request.operation === 'update' || request.operation === 'refine';
        // Recovery resumes the persisted operation at its existing revision.
        const expectedResult = incrementsRevision
          ? expected + 1
          : request.operation === 'prepare' && expected
          ? expected
          : undefined;
        const changedElsewhere = Boolean(
          current.stale ||
            (snapshot &&
              ((current.dirty.length && expectedResult && snapshot.revision > expectedResult) ||
                (!accepted && !knownPending && expected && snapshot.revision > expected) ||
                (accepted &&
                  incoming &&
                  snapshot.revision > incoming.revision &&
                  current.dirty.length)))
        );
        const dirty = settled && !changedElsewhere ? acknowledge(current, request) : current.dirty;
        const stale = changedElsewhere || Boolean(snapshot?.status === 'approved' && dirty.length);
        const rejectedDuringPreparation = Boolean(
          !accepted &&
            !knownPending &&
            snapshot?.status === 'preparing' &&
            incrementsRevision &&
            snapshot.revision === expected
        );
        const rejected =
          !stale &&
          !settled &&
          !rejectedDuringPreparation &&
          (incrementsRevision || request.operation === 'confirm') &&
          outcome.reviewFailure === 'rejected';
        if (settled || stale || rejectedDuringPreparation) {
          failedRequest.current = null;
          pendingPreparation.current = null;
          rejectedDraft.current = null;
        } else if (rejected) {
          failedRequest.current = null;
          pendingPreparation.current = null;
          rejectedDraft.current = submittedDraft;
        } else if (accepted && snapshot?.status === 'preparing') {
          // A live lease replay can finish streaming before generation finishes.
          // Keep its identity until a settled listener snapshot or retry arrives.
          pendingPreparation.current = { request, revision: snapshot.revision };
        }
        const draft = snapshot ? mergeDraft(current, snapshot, dirty) : current;
        const corrected =
          rejected &&
          editVersion.current > submittedEditVersion &&
          draftSignature(draft) !== submittedDraft;
        return {
          ...current,
          snapshot,
          values: draft.values,
          feedback: draft.feedback,
          dirty,
          busy: null,
          error: stale
            ? CHANGED
            : settled || accepted || corrected
            ? null
            : rejected
            ? REJECTED
            : request.operation === 'confirm' && snapshot?.status !== 'approved'
            ? CONFIRM_UNCERTAIN
            : ERROR,
          stale,
        };
      });
    },
    [projectId, userId, scope, send, update]
  );

  const save = useCallback(() => {
    const current = stateRef.current;
    // A correction reverted during the debounce is still the rejected draft.
    if (rejectedDraft.current && draftSignature(current) === rejectedDraft.current) {
      update(value => ({ ...value, error: value.stale ? CHANGED : REJECTED }));
      return;
    }
    if (
      !current.snapshot ||
      !current.values ||
      !current.dirty.length ||
      current.stale ||
      current.error ||
      inFlight.current ||
      current.snapshot.status === 'preparing' ||
      current.snapshot.status === 'approved'
    )
      return;
    const changes: ReviewChanges = {};
    for (const field of current.dirty) {
      if (field === 'feedbackDraft') changes.feedbackDraft = current.feedback;
      else if (field === 'subjectCompany') changes.subjectCompany = current.values.subjectCompany;
      else if (field === 'counterparties') changes.counterparties = current.values.counterparties;
      else changes.period = current.values.period;
    }
    void execute({
      operation: 'update',
      requestId: uuidv4(),
      expectedRevision: current.snapshot.revision,
      changes,
    });
  }, [execute, update]);

  useEffect(() => {
    if (
      !state.dirty.length ||
      state.busy ||
      state.error ||
      state.stale ||
      state.snapshot?.status === 'preparing'
    )
      return undefined;
    const timer = window.setTimeout(save, 500);
    return () => window.clearTimeout(timer);
  }, [
    state.dirty,
    state.values,
    state.feedback,
    state.busy,
    state.error,
    state.stale,
    state.snapshot?.status,
    state.snapshot?.revision,
    save,
  ]);

  const edit = useCallback(
    (values: ReviewValues, context?: { field: ReviewField; base: ReviewEditBase }) =>
      update(current => {
        if (
          !current.snapshot ||
          (context && context.base.scope !== current.scope) ||
          current.snapshot.status === 'approved' ||
          failedRequest.current?.request.operation === 'confirm' ||
          current.busy === 'refine' ||
          current.busy === 'confirm'
        )
          return current;
        const effective = context
          ? mergeDraft(current, current.snapshot, current.dirty).values
          : current.values;
        if (!effective) return current;
        const nextValues = context
          ? { ...effective, [context.field]: values[context.field] }
          : values;
        const fieldChangedElsewhere = Boolean(
          context &&
            ((!equal(context.base.values[context.field], effective?.[context.field]) &&
              !equal(values[context.field], effective?.[context.field])) ||
              (context.field === 'counterparties' &&
                context.base.counterpartyKind !== current.snapshot.counterpartyKind))
        );
        const dirty = new Set(current.dirty);
        for (const field of ['subjectCompany', 'counterparties', 'period'] as const) {
          if (
            (!context || context.field === field) &&
            !equal(nextValues[field], effective?.[field])
          )
            dirty.add(field);
        }
        const changed = !equal(nextValues, effective);
        if (changed) editVersion.current += 1;
        const next = { ...current, values: nextValues, dirty: Array.from(dirty) };
        const stale = current.stale || fieldChangedElsewhere;
        return {
          ...next,
          stale,
          error: stale
            ? CHANGED
            : rejectedDraft.current && changed
            ? draftSignature(next) === rejectedDraft.current
              ? REJECTED
              : null
            : current.error,
        };
      }),
    [update]
  );
  const editFeedback = useCallback(
    (feedback: string) =>
      update(current => {
        if (
          current.snapshot?.status === 'approved' ||
          failedRequest.current?.request.operation === 'confirm' ||
          current.busy === 'refine' ||
          current.busy === 'confirm'
        )
          return current;
        if (feedback === current.feedback) return current;
        editVersion.current += 1;
        const next = {
          ...current,
          feedback,
          dirty: Array.from(new Set<DirtyField>([...current.dirty, 'feedbackDraft'])),
        };
        return {
          ...next,
          error: current.stale
            ? CHANGED
            : rejectedDraft.current
            ? draftSignature(next) === rejectedDraft.current
              ? REJECTED
              : null
            : current.error,
        };
      }),
    [update]
  );
  const readyForAction = (current: EditorState) =>
    Boolean(
      current.snapshot &&
        !current.dirty.length &&
        !current.busy &&
        !current.stale &&
        !current.error &&
        current.snapshot.status !== 'preparing'
    );
  const refine = useCallback(() => {
    const current = stateRef.current;
    if (!readyForAction(current) || !current.snapshot?.canRefine || !current.feedback.trim())
      return;
    void execute({
      operation: 'refine',
      requestId: uuidv4(),
      expectedRevision: current.snapshot.revision,
    });
  }, [execute]);
  const confirm = useCallback(() => {
    const current = stateRef.current;
    if (!readyForAction(current) || !current.snapshot?.canConfirm) return;
    void execute({
      operation: 'confirm',
      requestId: uuidv4(),
      expectedRevision: current.snapshot.revision,
    });
  }, [execute]);
  const resolveConflict = useCallback(
    (resolution: ReviewResolution) => {
      const current = stateRef.current;
      if (!readyForAction(current) || !current.snapshot) return;
      void execute({
        operation: 'update',
        requestId: uuidv4(),
        expectedRevision: current.snapshot.revision,
        changes: {},
        resolutions: [resolution],
      });
    },
    [execute]
  );
  const retryRead = useCallback(() => setReadAttempt(n => n + 1), []);
  const retry = useCallback(() => {
    const current = stateRef.current;
    if (current.stale || inFlight.current) return;
    if (current.error === REJECTED) return;
    if (failedRequest.current) {
      void execute(failedRequest.current.request);
      return;
    }
    if (
      current.snapshot?.status === 'approved' &&
      current.snapshot.downstream.status !== 'completed'
    ) {
      void execute({
        operation: 'confirm',
        requestId: uuidv4(),
        expectedRevision: current.snapshot.revision,
      });
    } else if (
      current.snapshot?.status === 'preparing' ||
      current.snapshot?.status === 'needsAttention'
    ) {
      if (current.snapshot.status === 'needsAttention' && current.dirty.length) return;
      void execute({
        operation: 'prepare',
        requestId: uuidv4(),
        userQuery: current.snapshot.originalQuery,
      });
    } else retryRead();
  }, [execute, retryRead]);
  const reconcile = useCallback(
    (keepEdits: boolean) => {
      failedRequest.current = null;
      pendingPreparation.current = null;
      rejectedDraft.current = null;
      update(current => {
        if (!current.snapshot) return current;
        const dirty = keepEdits ? current.dirty : [];
        return {
          ...current,
          stale: false,
          error: null,
          dirty,
          ...mergeDraft(current, current.snapshot, dirty),
        };
      });
      const current = stateRef.current;
      if (keepEdits && current.snapshot?.status === 'preparing')
        void execute({
          operation: 'prepare',
          requestId: uuidv4(),
          userQuery: current.snapshot.originalQuery,
        });
    },
    [execute, update]
  );

  const current = state.scope === scope ? state : initial(scope);
  return {
    ...current,
    execute,
    edit,
    editFeedback,
    refine,
    confirm,
    resolveConflict,
    retry,
    retryRead,
    reconcile,
    readError: ['failed', 'denied', 'invalid'].includes(current.readStatus),
    actionReady: readyForAction(current),
    saving: current.busy === 'update' || current.dirty.length > 0,
    confirmationPending:
      state.scope === scope &&
      current.snapshot?.status !== 'approved' &&
      failedRequest.current?.request.operation === 'confirm',
    approved: current.snapshot?.status === 'approved',
    preparationComplete:
      current.snapshot?.status === 'approved' && current.snapshot.downstream.status === 'completed',
  };
}
